import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDownSharp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialogState } from '~/modules/common/hooks';
import { RoleAssignmentMode } from '~/types';
import RoleAssignmentDialog from '../../RoleAssignmentDialog';
import usePutTaskRoleAssignments from './usePutTaskRoleAssignments';

const useStyles = makeStyles(theme => ({
  accordion: {
    boxShadow: 'none',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    '&:before': { display: 'none' },
    margin: '0 !important'
  },
  accordionSummary: {
    flexDirection: 'row-reverse',
    padding: theme.spacing(0, 2, 0, 0.5),
    minHeight: theme.spacing(6),
    '&.Mui-expanded': { minHeight: theme.spacing(6) }
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: `${theme.spacing(1, 0)} !important`,
    width: '100%'
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    fontSize: '1.1rem'
  },
  details: {
    padding: theme.spacing(0, 2, 2)
  },
  roleField: {
    cursor: 'pointer'
  }
}));

const dialogTitle = (
  <FormattedMessage id="taskTimesheetAccessSection.selectTaskRolesTitle" />
);

const EDIT_TASK_ROLES_RADIO_OPTIONS = [
  {
    value: RoleAssignmentMode.UserProfileRoles,
    labelId: 'selectTaskRolesDialog.userProfileRoles',
    subtitleId: 'selectTaskRolesDialog.userProfileRolesSubtitle'
  },
  {
    value: RoleAssignmentMode.SpecificRoles,
    labelId: 'selectTaskRolesDialog.specificRoles',
    subtitleId: 'selectTaskRolesDialog.specificRolesSubtitle'
  }
];

const getTaskRoleDisplayText = (summary, formatMessage) => {
  if (!summary || summary.totalRoleCount === 0) {
    return formatMessage({ id: 'selectTaskRolesDialog.userProfileRoles' });
  }
  const { defaultRole, totalRoleCount } = summary;

  if (!defaultRole) {
    const noDefaultRole = formatMessage({
      id: 'selectTaskRolesDialog.noDefaultRole'
    });

    return `${noDefaultRole} +${totalRoleCount}`;
  }

  return totalRoleCount > 1
    ? `${defaultRole.displayText} +${totalRoleCount - 1}`
    : defaultRole.displayText;
};

const TaskTimesheetAccessSection = ({ task }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const { open, openDialog, closeDialog } = useDialogState();

  const {
    putTaskRoleAssignments,
    loading: isSaving
  } = usePutTaskRoleAssignments();

  const initialMode =
    task.taskRoleAssignmentSummary?.roleAssignmentMode ?? null;

  const handleTaskRolesClick = useCallback(() => {
    openDialog();
  }, [openDialog]);

  const handleSave = useCallback(
    ({
      roleAssignmentMode: mode,
      rolesToAssign,
      roleUrisToUnassign,
      defaultRole,
      totalRoleCount
    }) =>
      putTaskRoleAssignments({
        taskUri: task.id,
        mode,
        rolesToAssign,
        roleUrisToUnassign,
        defaultRole,
        totalRoleCount
      }),
    [putTaskRoleAssignments, task.id]
  );

  const taskRoleDisplayText = getTaskRoleDisplayText(
    task.taskRoleAssignmentSummary,
    formatMessage
  );

  return (
    <Accordion className={classes.accordion} defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.accordionSummary}
        classes={{ content: classes.summaryContent }}
      >
        <Typography variant="subtitle1" className={classes.title}>
          <FormattedMessage id="taskTimesheetAccessSection.title" />
        </Typography>
        <Tooltip
          title={<FormattedMessage id="taskTimesheetAccessSection.title" />}
        >
          <InfoOutlinedIcon className={classes.infoIcon} />
        </Tooltip>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label={formatMessage({
                id: 'taskTimesheetAccessSection.label'
              })}
              value={taskRoleDisplayText}
              variant="outlined"
              fullWidth
              className={classes.roleField}
              onClick={handleTaskRolesClick}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <ArrowDropDownIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
      <RoleAssignmentDialog
        open={open}
        onClose={closeDialog}
        onSave={handleSave}
        isSaving={isSaving}
        taskUri={task.id}
        variant="taskRole"
        title={dialogTitle}
        radioOptions={EDIT_TASK_ROLES_RADIO_OPTIONS}
        initialMode={initialMode}
        savedDefaultRole={task.taskRoleAssignmentSummary?.defaultRole}
        savedRoleCount={task.taskRoleAssignmentSummary?.totalRoleCount}
      />
    </Accordion>
  );
};

TaskTimesheetAccessSection.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string.isRequired,
    taskRoleAssignmentSummary: PropTypes.shape({
      roleAssignmentMode: PropTypes.string,
      defaultRole: PropTypes.shape({
        id: PropTypes.string,
        displayText: PropTypes.string
      }),
      totalRoleCount: PropTypes.number
    })
  }).isRequired
};

export default TaskTimesheetAccessSection;
